import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";
import { api } from "../../lib/api";
import { SearchableSelect } from "../../components/SearchableSelect";

export default function AdminReferencePage() {
    const { t } = useTranslation();
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [countryId, setCountryId] = useState("");
    const [stateId, setStateId] = useState("");

    const load = async () => {
        const [c, cat] = await Promise.all([api.get("/api/countries"), api.get("/api/categories")]);
        setCountries(c.data || []);
        setCategories(cat.data || []);
    };
    useEffect(() => { load(); }, []);

    useEffect(() => {
        if (countryId) api.get("/api/states", { params: { country_id: countryId } }).then(({ data }) => { setStates(data || []); setStateId(""); setCities([]); });
        else setStates([]);
    }, [countryId]);
    useEffect(() => {
        if (stateId) api.get("/api/cities", { params: { state_id: stateId } }).then(({ data }) => setCities(data || []));
        else setCities([]);
    }, [stateId]);

    return (
        <div data-testid="admin-reference-page" className="space-y-8">
            <h1 className="font-heading font-black text-3xl">Reference Data</h1>

            <Section title={`${t("admin.states")} & ${t("admin.cities")}`}>
                <div className="mb-3 max-w-sm">
                    <label className="text-xs font-semibold block mb-1">{t("search.country")}</label>
                    <SearchableSelect
                        testId="ref-country"
                        options={countries.map((c) => ({ value: c.id, label: c.name }))}
                        value={countryId}
                        onValueChange={setCountryId}
                        placeholder={t("common.choose")}
                    />
                </div>
                {countryId && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <StateManager countryId={countryId} states={states} onChange={() => api.get("/api/states", { params: { country_id: countryId } }).then(({ data }) => setStates(data || []))} onSelect={setStateId} selectedId={stateId} />
                        {stateId && <CityManager stateId={stateId} cities={cities} onChange={() => api.get("/api/cities", { params: { state_id: stateId } }).then(({ data }) => setCities(data || []))} />}
                    </div>
                )}
            </Section>

            <Section title={t("admin.categories")}>
                <CategoryManager categories={categories} onChange={load} />
            </Section>
        </div>
    );
}

const Section = ({ title, children }) => (
    <div>
        <h2 className="font-heading font-bold text-xl mb-3">{title}</h2>
        <div className="bento-card p-5">{children}</div>
    </div>
);

const StateManager = ({ countryId, states, onChange, onSelect, selectedId }) => {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const add = async () => {
        if (!name) return;
        await api.post("/api/admin/states", { name, country_id: countryId });
        setName("");
        onChange();
    };
    const remove = async (id) => {
        try { await api.delete(`/api/admin/states/${id}`); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
    };
    return (
        <div className="space-y-2">
            <h3 className="font-semibold">{t("admin.states")}</h3>
            <div className="flex gap-2">
                <input data-testid="state-name-input" value={name} onChange={(e) => setName(e.target.value)} className="input-modern !py-2 flex-1" placeholder={t("admin.name")} />
                <button data-testid="state-add-btn" onClick={add} className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-1"><Plus className="h-4 w-4" /></button>
            </div>
            <ul className="space-y-1 max-h-72 overflow-y-auto">
                {states.map((s) => (
                    <li key={s.id} data-testid={`state-${s.id}`} className={`flex items-center justify-between p-2 rounded-lg cursor-pointer ${selectedId === s.id ? "bg-foreground text-background" : "hover:bg-muted"}`} onClick={() => onSelect(s.id)}>
                        <span className="text-sm font-semibold">{s.name}</span>
                        <button onClick={(e) => { e.stopPropagation(); remove(s.id); }} className="p-1 hover:bg-destructive/10 rounded"><Trash2 className="h-3 w-3" /></button>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const CityManager = ({ stateId, cities, onChange }) => {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const add = async () => {
        if (!name) return;
        await api.post("/api/admin/cities", { name, state_id: stateId });
        setName("");
        onChange();
    };
    const remove = async (id) => {
        try { await api.delete(`/api/admin/cities/${id}`); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
    };
    return (
        <div className="space-y-2">
            <h3 className="font-semibold">{t("admin.cities")}</h3>
            <div className="flex gap-2">
                <input data-testid="city-name-input" value={name} onChange={(e) => setName(e.target.value)} className="input-modern !py-2 flex-1" placeholder={t("admin.name")} />
                <button data-testid="city-add-btn" onClick={add} className="btn-primary !py-2 !px-4 text-sm"><Plus className="h-4 w-4" /></button>
            </div>
            <ul className="space-y-1 max-h-72 overflow-y-auto">
                {cities.map((c) => (
                    <li key={c.id} data-testid={`city-${c.id}`} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted">
                        <span className="text-sm font-semibold">{c.name}</span>
                        <button onClick={() => remove(c.id)} className="p-1 hover:bg-destructive/10 rounded"><Trash2 className="h-3 w-3" /></button>
                    </li>
                ))}
            </ul>
        </div>
    );
};

const CategoryManager = ({ categories, onChange }) => {
    const { t } = useTranslation();
    const [name, setName] = useState("");
    const add = async () => {
        if (!name) return;
        await api.post("/api/admin/categories", { name });
        setName("");
        onChange();
    };
    const remove = async (id) => {
        try { await api.delete(`/api/admin/categories/${id}`); onChange(); } catch (e) { toast.error(e?.response?.data?.detail || t("common.error")); }
    };
    return (
        <div className="space-y-2">
            <div className="flex gap-2 max-w-md">
                <input data-testid="cat-name-input" value={name} onChange={(e) => setName(e.target.value)} className="input-modern !py-2 flex-1" placeholder={t("admin.name")} />
                <button data-testid="cat-add-btn" onClick={add} className="btn-primary !py-2 !px-4 text-sm"><Plus className="h-4 w-4" /></button>
            </div>
            <div className="flex flex-wrap gap-2">
                {categories.map((c) => (
                    <span key={c.id} data-testid={`cat-${c.id}`} className="badge-pill bg-muted flex items-center gap-2">
                        {c.name}
                        <button onClick={() => remove(c.id)} className="hover:text-destructive"><Trash2 className="h-3 w-3" /></button>
                    </span>
                ))}
            </div>
        </div>
    );
};
